import * as React from 'react';
import { Typography, Button, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import DomainBadge from '../Authorize/domain-badge';
import { useConsentStyles } from '../Authorize/styles';
import { scopeLabel } from '../Authorize/translations';

const useStyles = makeStyles((theme) => ({
  item: { padding: theme.spacing(2, 0) },
  name: { fontWeight: 700 },
  section: { marginTop: 12 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  // Long fr/es scope labels wrap inside the chip instead of overflowing.
  chip: { height: 'auto', maxWidth: '100%', padding: '4px 0' },
  chipLabel: { whiteSpace: 'normal' },
  // xs: full width, away from the text; sm+: on the right.
  actions: {
    marginTop: 12,
    '& > *': { width: '100%' },
    [theme.breakpoints.up('sm')]: { textAlign: 'right', '& > *': { width: 'auto' } },
  },
}));

// One connected app: untrusted name, domain in evidence, the granted scopes
// with the consent screen labels, when it was connected and last used, and
// the way to revoke it. No client logo (untrusted image).
export default function GrantItem({ grant, index, disabled, onRevoke, buttonRef }) {
  const { t } = useTranslation();
  // Same 'datetime-format' as hooks/useDateFormat, in the browser's time zone.
  // (That hook's namespace import of moment cannot be called under Jest.)
  const formatDateTime = (value) => moment(value).format(t('datetime-format'));
  const consent = useConsentStyles();
  const classes = useStyles();
  const nameId = `connected-app-${index}-name`;

  return (
    <li className={`list-group-item ${classes.item}`}>
      {/* Not h6: the theme lowercases h6 and this is an untrusted proper noun. */}
      <Typography id={nameId} variant="subtitle1" component="h2" className={`${consent.literal} ${classes.name}`}>
        {grant.client.name}
      </Typography>
      <DomainBadge domain={grant.client.domain} />

      <Typography variant="caption" color="textSecondary" component="div" className={classes.section}>
        {t('mcp.connectedApps.permissionsLabel')}
      </Typography>
      <div className={classes.chips}>
        {grant.scopes.map((scope) => (
          <Chip
            key={scope}
            size="small"
            variant="outlined"
            label={scopeLabel(t, { scope })}
            className={classes.chip}
            classes={{ label: `${consent.literal} ${classes.chipLabel}` }}
          />
        ))}
      </div>

      {/* One datum per line: the fr strings are long. */}
      <Typography variant="caption" color="textSecondary" component="div" className={classes.section}>
        {t('mcp.connectedApps.connectedOn', { date: formatDateTime(grant.created_at) })}
      </Typography>
      <Typography variant="caption" color="textSecondary" component="div">
        {grant.last_used_at
          ? t('mcp.connectedApps.lastUsed', { date: formatDateTime(grant.last_used_at) })
          : t('mcp.connectedApps.neverUsed')}
      </Typography>

      <div className={classes.actions}>
        <Button
          ref={buttonRef}
          type="button"
          variant="outlined"
          size="small"
          className={consent.revoke}
          aria-describedby={nameId}
          disabled={disabled}
          onClick={() => onRevoke(grant)}
        >
          {t('mcp.connectedApps.revoke')}
        </Button>
      </div>
    </li>
  );
}
